import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { UserCheck } from "lucide-react";

const CAN_ASSIGN = ["admin", "direzione", "segreteria"];

export function RequestAssign({ req, user }) {
  const qc = useQueryClient();
  const canAssign = CAN_ASSIGN.includes(user?.role);
  const { data: assignees = [] } = useQuery({
    queryKey: ["helpdesk-assignees"],
    queryFn: async () => (await api.get("/helpdesk/assignees")).data,
    enabled: canAssign,
  });
  const mut = useMutation({
    mutationFn: async (assigned_to) => (await api.patch(`/requests/${req.id}/assign`, { assigned_to })).data,
    onSuccess: (d) => {
      toast.success(d.assigned_to ? `Assegnata a ${d.assigned_to_name}` : "Assegnazione rimossa");
      qc.invalidateQueries({ queryKey: ["request", req.id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none" data-testid="request-assign-card">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><UserCheck size={12}/> Assegnata a</div>
      {canAssign ? (
        <Select value={req.assigned_to || "__none"} onValueChange={(v) => mut.mutate(v === "__none" ? null : v)} disabled={mut.isPending}>
          <SelectTrigger className="mt-2" data-testid="request-assignee-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">Non assegnata</SelectItem>
            {assignees.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`request-assignee-opt-${a.id}`}>{a.name || a.email} · {a.role}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div className="mt-2 text-sm font-medium" data-testid="request-assignee-readonly">{req.assigned_to_name || "Non assegnata"}</div>
      )}
      {req.assigned_to && req.assigned_by_name && (
        <div className="text-[11px] text-stone-400 mt-1.5">da {req.assigned_by_name}{req.assigned_at ? ` · ${fmtDate(req.assigned_at)}` : ""}{req.assigned_to !== user?.id ? " · riceve una notifica" : ""}</div>
      )}
    </Card>
  );
}
